import type { PricingList } from 'api/types/pricing.types';
import { isPerBagService } from 'api/types/pricing.types';
import { CPBag, HCBag, PBag, WFBag } from 'assets/icons';
import cx from 'classnames';
import { useBagServices } from 'hooks/useBagServices';
import React, { useMemo } from 'react';
import { Row } from 'react-bootstrap';
import { Trans } from 'react-i18next';
import useCartEstimator, { useItemQuantity } from 'store/cart-estimator';
import styled from 'styled-components';

import AddButton from '../addButton';
import styles from './styles.module.scss';

const BagServiceMapper = new Map<PricingList.ServiceTypes, React.ReactElement>([
  ['HOME_CARE', <HCBag />],
  ['PRESS', <PBag />],
  ['CLEAN_PRESS', <CPBag />],
  ['WASH_FOLD', <WFBag />],
]);

interface HintItemProps {
  hintItem: PricingList.HintItem;
}

const StyledSpan = styled.span<{ hasDiscountedPrice?: boolean }>`
  margin-right: 2px;
  font-weight: ${({ hasDiscountedPrice = false }) => (hasDiscountedPrice ? 400 : 500)};
  text-decoration: ${({ hasDiscountedPrice = false }) => (hasDiscountedPrice ? 'line-through' : undefined)};
`;

const HintItem: React.FC<HintItemProps> = ({ hintItem }) => {
  const { title, description = '', showBagIcon, bagType } = hintItem;
  const bagIcon = BagServiceMapper.get(bagType);

  const add = useCartEstimator.use.add();
  const remove = useCartEstimator.use.remove();

  const { services } = useBagServices();

  const [, serviceDetails] = useMemo(
    () => services.find(([service]) => service === hintItem.bagType) ?? [undefined, undefined],
    [hintItem.bagType, services]
  );

  const quantity = useItemQuantity(hintItem.bagType, serviceDetails?.name || '');

  const getDescription = React.useCallback(() => {
    let desc = description;
    desc = desc.substring(0, desc.lastIndexOf(' '));
    desc = desc.substring(0, desc.lastIndexOf(' '));

    if (serviceDetails && serviceDetails.discountedPrice === undefined) {
      desc += ` AED ${serviceDetails?.price}`;
    }
    if (serviceDetails && serviceDetails.discountedPrice) {
      desc += ` <1>AED ${serviceDetails?.price}</1> <2>AED ${serviceDetails.discountedPrice}</2>`;
    }

    return desc;
  }, [description, serviceDetails]);

  if ((!title && !description) || !serviceDetails) return null;
  return (
    <div className={cx('mb-2', styles.hintItem, styles[bagType])}>
      <div className={cx('d-flex align-items-center', styles.leftSection)}>{showBagIcon && bagIcon}</div>
      <div className={styles.midSection}>
        {description && (
          <Row noGutters>
            <p className={cx(styles.description)}>
              <Trans
                i18nKey="fallbackToDefault"
                defaults={getDescription()}
                components={[<span className="font-weight-bold" />, <StyledSpan hasDiscountedPrice />, <StyledSpan />]}
              />
            </p>
          </Row>
        )}
      </div>
      {isPerBagService(serviceDetails) && (
        <div className="d-flex align-items-end pr-7">
          <AddButton
            item={{
              name: 'Bags',
              price: serviceDetails.price,
              discountedPrice: serviceDetails.discountedPrice,
              key: serviceDetails.name,
              quantity,
              category: serviceDetails.name,
            }}
            add={add}
            remove={remove}
            serviceType={bagType}
            customColor={bagType === 'HOME_CARE' ? '#FF81FF' : undefined}
          />
        </div>
      )}
    </div>
  );
};

export default HintItem;
